import { useEffect, useState } from 'react'
import { fetchWithAuth } from '@/lib/api'
import { WORKSPACE_EXTENSIONS_CHANGED } from '@/hooks/use-workspace-extensions'

type ExtensionRow = { extension_id: string; enabled: boolean }

function idsWithExtension(
  workspaceIds: string[],
  results: ExtensionRow[][],
  extensionId: string,
): string[] {
  return workspaceIds.filter((_, index) => {
    const ext = results[index]?.find((row) => row.extension_id === extensionId)
    return Boolean(ext?.enabled)
  })
}

async function loadExtensionLists(workspaceIds: string[]): Promise<ExtensionRow[][]> {
  return Promise.all(
    workspaceIds.map(async (id) => {
      try {
        const response = await fetchWithAuth(`/api/organizations/${id}/extensions`)
        if (!response.ok) return []
        return (await response.json()) as ExtensionRow[]
      } catch {
        return []
      }
    }),
  )
}

/**
 * Rule 4 gate: true when at least one workspace in the list has the extension enabled.
 * The icon stays hidden until the first answer. Later refreshes keep that answer
 * so a reload does not hide the rail and snap the sidebar back to Browse.
 * `enabledIds` is the subset that has the extension on. It is not cleared while a refresh runs.
 */
export function useExtensionEnabledInAnyWorkspace(
  extensionId: string,
  workspaceIds: string[],
) {
  const [enabled, setEnabled] = useState(false)
  const [enabledIds, setEnabledIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const idsKey = workspaceIds.join(',')

  useEffect(() => {
    let cancelled = false

    async function run() {
      if (workspaceIds.length === 0) {
        setEnabled(false)
        setEnabledIds([])
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const results = await loadExtensionLists(workspaceIds)
        if (cancelled) return
        const ids = idsWithExtension(workspaceIds, results, extensionId)
        setEnabledIds(ids)
        setEnabled(ids.length > 0)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [extensionId, idsKey, workspaceIds])

  useEffect(() => {
    const handler = () => {
      setLoading(true)
      void (async () => {
        try {
          const results = await loadExtensionLists(workspaceIds)
          const ids = idsWithExtension(workspaceIds, results, extensionId)
          setEnabledIds(ids)
          setEnabled(ids.length > 0)
        } finally {
          setLoading(false)
        }
      })()
    }
    window.addEventListener(WORKSPACE_EXTENSIONS_CHANGED, handler)
    return () => window.removeEventListener(WORKSPACE_EXTENSIONS_CHANGED, handler)
  }, [extensionId, idsKey, workspaceIds])

  // First load stays hidden (`enabled` starts false). A refresh keeps the last
  // answer. Reporting disabled while `loading` was kicking the Nextcloud rail
  // back to Browse whenever this hook refetched.
  return { enabled, enabledIds, loading }
}
